import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { AppIcon } from '../../icons/appIcons.tsx';

interface RanchGirl {
  id: string;
  firstName: string;
  lastName: string;
  age: number;
  dateOfBirth: string;
  skillLevel: string;
  horseAssigned: string;
  assignedHorse?: {
    id: string;
    name: string;
    breed: string;
    age: number;
    color: string;
    imageUrl: string;
  };
  memberSince: string;
  graduationYear: number;
  allergies: string;
  medicalNotes: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  boardPosition: string;
}

interface FamilyData {
  id: string;
  familyName: string;
  primaryContactFirstName: string;
  primaryContactLastName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  textOptInPrimary: boolean;
  secondaryContactFirstName: string;
  secondaryContactLastName: string;
  secondaryContactEmail: string;
  secondaryContactPhone: string;
  textOptInSecondary: boolean;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  membershipStatus: string;
  monthlyFee: number;
  joinDate: string;
  ranchGirls: RanchGirl[];
}

export default function FamilyProfile() {
  const [familyData, setFamilyData] = useState<FamilyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingFamily, setEditingFamily] = useState(false);
  const [editingRanchGirl, setEditingRanchGirl] = useState<string | null>(null);
  const [editedFamilyData, setEditedFamilyData] = useState<FamilyData | null>(null);
  const [editedRanchGirls, setEditedRanchGirls] = useState<{[key: string]: RanchGirl}>({});
  const [saveMessage, setSaveMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const { user } = useAuth();

  useEffect(() => {
    fetchFamilyProfile();
  }, []);

  useEffect(() => {
    if (saveMessage) {
      const timer = setTimeout(() => setSaveMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [saveMessage]);

  const fetchFamilyProfile = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/families/profile', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setFamilyData(data.data);
        setEditedFamilyData(data.data);
      }
    } catch (error) {
      console.error('Error fetching family profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFamilyInfo = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/families/profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editedFamilyData)
      });
      
      const data = await response.json();
      if (data.success) {
        setFamilyData(data.data);
        setEditedFamilyData(data.data);
        setEditingFamily(false);
        setSaveMessage({ type: 'success', text: 'Family information updated successfully!' });
      } else {
        setSaveMessage({ type: 'error', text: data.message || 'Failed to update family information' });
      }
    } catch (error) {
      console.error('Error updating family profile:', error);
      setSaveMessage({ type: 'error', text: 'An error occurred while updating' });
    }
  };

  const handleSaveRanchGirl = async (girlId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/ranchgirls/${girlId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editedRanchGirls[girlId])
      });
      
      const data = await response.json();
      if (data.success) {
        // Update the family data with the updated ranch girl
        if (familyData) {
          const updatedRanchGirls = familyData.ranchGirls.map(girl =>
            girl.id === girlId ? { ...girl, ...editedRanchGirls[girlId] } : girl
          );
          setFamilyData({ ...familyData, ranchGirls: updatedRanchGirls });
        }
        setEditingRanchGirl(null);
        const newEditedGirls = { ...editedRanchGirls };
        delete newEditedGirls[girlId];
        setEditedRanchGirls(newEditedGirls);
        setSaveMessage({ type: 'success', text: 'Ranch girl information updated successfully!' });
      } else {
        setSaveMessage({ type: 'error', text: data.message || 'Failed to update ranch girl information' });
      }
    } catch (error) {
      console.error('Error updating ranch girl:', error);
      setSaveMessage({ type: 'error', text: 'An error occurred while updating' });
    }
  };

  const handleFamilyInputChange = (field: keyof FamilyData, value: string | number) => {
    if (editedFamilyData) {
      setEditedFamilyData({
        ...editedFamilyData,
        [field]: value
      });
    }
  };

  const handleRanchGirlInputChange = (girlId: string, field: keyof RanchGirl, value: string | number) => {
    const girl = familyData?.ranchGirls.find(g => g.id === girlId);
    if (girl) {
      setEditedRanchGirls({
        ...editedRanchGirls,
        [girlId]: {
          ...(editedRanchGirls[girlId] || girl),
          [field]: value
        }
      });
    }
  };

  const startEditingRanchGirl = (girlId: string) => {
    const girl = familyData?.ranchGirls.find(g => g.id === girlId);
    if (girl) {
      setEditedRanchGirls({
        ...editedRanchGirls,
        [girlId]: { ...girl }
      });
      setEditingRanchGirl(girlId);
    }
  };

  const cancelEditingRanchGirl = (girlId: string) => {
    const newEditedGirls = { ...editedRanchGirls };
    delete newEditedGirls[girlId];
    setEditedRanchGirls(newEditedGirls);
    setEditingRanchGirl(null);
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setSaveMessage({ type: 'error', text: 'New passwords do not match!' });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setSaveMessage({ type: 'error', text: 'Password must be at least 6 characters long!' });
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      const data = await response.json();
      if (data.success) {
        setSaveMessage({ type: 'success', text: 'Password changed successfully!' });
        setShowPasswordModal(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setSaveMessage({ type: 'error', text: data.message || 'Failed to change password' });
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setSaveMessage({ type: 'error', text: 'An error occurred while changing password' });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-ggaa-green"></div>
      </div>
    );
  }

  if (!familyData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">No family profile found.</p>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-6 lg:py-10">
      {/* Header with Status Message */}
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">Family Profile</h1>
        <p className="text-sm sm:text-base text-gray-600">Manage your family information and ranch girl profiles</p>
        
        {saveMessage && (
          <div className={`mt-4 p-3 sm:p-4 rounded-lg ${
            saveMessage.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}>
            <p className={`text-sm sm:text-base font-medium ${
              saveMessage.type === 'success' ? 'text-green-800' : 'text-red-800'
            }`}>
              {saveMessage.text}
            </p>
          </div>
        )}
      </div>

      {/* Account Information Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg sm:rounded-2xl shadow-xl p-4 sm:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-white mb-2">Account Information</h2>
            <div className="text-blue-50">
              <span className="text-xs sm:text-sm opacity-75 block">Login Email:</span>
              <p className="font-mono text-sm sm:text-lg font-semibold break-all">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={() => setShowPasswordModal(true)}
            className="bg-white text-blue-600 px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-blue-50 transition-colors font-semibold shadow-md flex items-center justify-center gap-2 text-sm sm:text-base whitespace-nowrap flex-shrink-0"
          >
            <AppIcon name="lock" className="w-4 h-4" />
            <span className="hidden sm:inline">Change Password</span><span className="sm:hidden">Change</span>
          </button>
        </div>
      </div>

      {/* Family Information Card */}
      <div className="bg-white rounded-lg sm:rounded-2xl shadow-xl border-2 border-ggaa-green/30 overflow-hidden mb-8">
        <div className="bg-gradient-to-r from-ggaa-green to-ggaa-green/80 px-4 sm:px-8 py-4 sm:py-5">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold text-white break-words">The {familyData.familyName} Family</h2>
              <p className="text-sm sm:text-base text-green-50">Member Since: {new Date(familyData.joinDate).toLocaleDateString()}</p>
            </div>
            {!editingFamily ? (
              <button
                onClick={() => setEditingFamily(true)}
                className="bg-white text-ggaa-green px-4 sm:px-6 py-2 rounded-lg hover:bg-green-50 transition-colors font-semibold shadow-md text-sm sm:text-base whitespace-nowrap flex-shrink-0"
              >
                <span className="inline-flex items-center gap-1.5">
                  <AppIcon name="edit" className="w-4 h-4" />
                  Edit<span className="hidden sm:inline"> Family Info</span>
                </span>
              </button>
            ) : (
              <div className="flex gap-2 sm:gap-3">
                <button
                  onClick={() => {
                    setEditingFamily(false);
                    setEditedFamilyData(familyData);
                  }}
                  className="bg-gray-200 text-gray-700 px-3 sm:px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors font-semibold text-sm sm:text-base flex-1 sm:flex-none"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveFamilyInfo}
                  className="bg-white text-ggaa-green px-3 sm:px-6 py-2 rounded-lg hover:bg-green-50 transition-colors font-semibold shadow-md text-sm sm:text-base flex-1 sm:flex-none whitespace-nowrap"
                >
                  <span className="hidden sm:inline">Save Changes</span><span className="sm:hidden">Save</span>
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="p-4 sm:p-6 lg:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Primary Contact */}
            <div className="space-y-4 sm:space-y-6">
              <h3 className="text-lg sm:text-xl font-semibold text-ggaa-brown border-b-2 border-ggaa-green/30 pb-2">
                <span className="inline-flex items-center gap-2">
                  <AppIcon name="profile" className="w-5 h-5" />
                  Primary Contact
                </span>
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
                  {editingFamily ? (
                    <input
                      type="text"
                      value={editedFamilyData?.primaryContactFirstName || ''}
                      onChange={(e) => handleFamilyInputChange('primaryContactFirstName', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 font-medium py-2">{familyData.primaryContactFirstName}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
                  {editingFamily ? (
                    <input
                      type="text"
                      value={editedFamilyData?.primaryContactLastName || ''}
                      onChange={(e) => handleFamilyInputChange('primaryContactLastName', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 font-medium py-2">{familyData.primaryContactLastName}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                {editingFamily ? (
                  <input
                    type="email"
                    value={editedFamilyData?.primaryContactEmail || ''}
                    onChange={(e) => handleFamilyInputChange('primaryContactEmail', e.target.value)}
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 font-medium py-2 break-all">{familyData.primaryContactEmail}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                {editingFamily ? (
                  <input
                    type="tel"
                    value={editedFamilyData?.primaryContactPhone || ''}
                    onChange={(e) => handleFamilyInputChange('primaryContactPhone', e.target.value)}
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 font-medium py-2">{familyData.primaryContactPhone}</p>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingFamily ? editedFamilyData?.textOptInPrimary : familyData.textOptInPrimary}
                    onChange={(e) => editingFamily && handleFamilyInputChange('textOptInPrimary', e.target.checked)}
                    disabled={!editingFamily}
                    className="h-5 w-5 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
                  />
                  <div>
                    <span className="block font-semibold text-gray-900 inline-flex items-center gap-2">
                      <AppIcon name="textMessage" className="w-4 h-4" />
                      Opt in for Text Notifications
                    </span>
                    <span className="block text-sm text-gray-600">Receive important updates via SMS</span>
                  </div>
                </label>
              </div>
            </div>

            {/* Secondary Contact */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-ggaa-brown border-b-2 border-ggaa-green/30 pb-2">
                <span className="inline-flex items-center gap-2">
                  <AppIcon name="members" className="w-5 h-5" />
                  Secondary Contact
                </span>
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
                  {editingFamily ? (
                    <input
                      type="text"
                      value={editedFamilyData?.secondaryContactFirstName || ''}
                      onChange={(e) => handleFamilyInputChange('secondaryContactFirstName', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                      placeholder="Optional"
                    />
                  ) : (
                    <p className="text-gray-900 font-medium py-2">{familyData.secondaryContactFirstName || '-'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
                  {editingFamily ? (
                    <input
                      type="text"
                      value={editedFamilyData?.secondaryContactLastName || ''}
                      onChange={(e) => handleFamilyInputChange('secondaryContactLastName', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                      placeholder="Optional"
                    />
                  ) : (
                    <p className="text-gray-900 font-medium py-2">{familyData.secondaryContactLastName || '-'}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                {editingFamily ? (
                  <input
                    type="email"
                    value={editedFamilyData?.secondaryContactEmail || ''}
                    onChange={(e) => handleFamilyInputChange('secondaryContactEmail', e.target.value)}
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                    placeholder="Optional"
                  />
                ) : (
                  <p className="text-gray-900 font-medium py-2 break-all">{familyData.secondaryContactEmail || '-'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                {editingFamily ? (
                  <input
                    type="tel"
                    value={editedFamilyData?.secondaryContactPhone || ''}
                    onChange={(e) => handleFamilyInputChange('secondaryContactPhone', e.target.value)}
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                    placeholder="Optional"
                  />
                ) : (
                  <p className="text-gray-900 font-medium py-2">{familyData.secondaryContactPhone || '-'}</p>
                )}
              </div>

              {familyData.secondaryContactPhone && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingFamily ? editedFamilyData?.textOptInSecondary : familyData.textOptInSecondary}
                      onChange={(e) => editingFamily && handleFamilyInputChange('textOptInSecondary', e.target.checked)}
                      disabled={!editingFamily}
                      className="h-5 w-5 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
                    />
                    <div>
                      <span className="block font-semibold text-gray-900 inline-flex items-center gap-2">
                      <AppIcon name="textMessage" className="w-4 h-4" />
                      Opt in for Text Notifications
                    </span>
                      <span className="block text-sm text-gray-600">Receive important updates via SMS</span>
                    </div>
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Address Section */}
          <div className="mt-8 pt-8 border-t-2 border-gray-200">
            <h3 className="text-xl font-semibold text-ggaa-brown border-b-2 border-ggaa-green/30 pb-2 mb-6">
              <span className="inline-flex items-center gap-2">
                <AppIcon name="home" className="w-5 h-5" />
                Address
              </span>
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Street Address</label>
                {editingFamily ? (
                  <input
                    type="text"
                    value={editedFamilyData?.address || ''}
                    onChange={(e) => handleFamilyInputChange('address', e.target.value)}
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 font-medium py-2">{familyData.address}</p>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">City</label>
                  {editingFamily ? (
                    <input
                      type="text"
                      value={editedFamilyData?.city || ''}
                      onChange={(e) => handleFamilyInputChange('city', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 font-medium py-2">{familyData.city}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">State</label>
                  {editingFamily ? (
                    <input
                      type="text"
                      value={editedFamilyData?.state || ''}
                      onChange={(e) => handleFamilyInputChange('state', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 font-medium py-2">{familyData.state}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">ZIP Code</label>
                  {editingFamily ? (
                    <input
                      type="text"
                      value={editedFamilyData?.zipCode || ''}
                      onChange={(e) => handleFamilyInputChange('zipCode', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 font-medium py-2">{familyData.zipCode}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Membership Info - Read Only */}
          <div className="mt-8 pt-8 border-t-2 border-gray-200">
            <h3 className="text-xl font-semibold text-ggaa-brown border-b-2 border-ggaa-green/30 pb-2 mb-6">
              <span className="inline-flex items-center gap-2">
                <AppIcon name="membership" className="w-5 h-5" />
                Membership Information
              </span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-ggaa-cream rounded-lg p-4">
                <label className="block text-sm font-semibold text-gray-600 mb-1">Status</label>
                <p className="text-lg font-bold text-ggaa-green capitalize">{familyData.membershipStatus}</p>
              </div>
              <div className="bg-ggaa-cream rounded-lg p-4">
                <label className="block text-sm font-semibold text-gray-600 mb-1">Monthly Fee</label>
                <p className="text-lg font-bold text-ggaa-brown">${familyData.monthlyFee}/month</p>
              </div>
              <div className="bg-ggaa-cream rounded-lg p-4">
                <label className="block text-sm font-semibold text-gray-600 mb-1">Member Since</label>
                <p className="text-lg font-bold text-gray-900">{new Date(familyData.joinDate).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ranch Girls Section */}
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2">
          <AppIcon name="horse" className="w-7 h-7 text-ggaa-green" />
          Our Ranch Girls
        </h2>
        
        {familyData.ranchGirls && familyData.ranchGirls.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {familyData.ranchGirls.map((girl) => {
              const isEditing = editingRanchGirl === girl.id;
              const editedGirl = editedRanchGirls[girl.id] || girl;

              return (
                <div key={girl.id} className="bg-white rounded-lg sm:rounded-2xl shadow-xl border-2 border-ggaa-green/30 overflow-hidden">
                  <div className="bg-gradient-to-r from-ggaa-brown to-ggaa-brown/80 px-4 sm:px-6 py-3 sm:py-4">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg sm:text-2xl font-bold text-white break-words">
                          {girl.firstName} {girl.lastName}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-100">{girl.age} years old</p>
                      </div>
                      {!isEditing ? (
                        <button
                          onClick={() => startEditingRanchGirl(girl.id)}
                          className="bg-white text-ggaa-brown px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-sm shadow-md flex-shrink-0"
                        >
                          Edit
                        </button>
                      ) : (
                        <div className="flex gap-2">
                          <button
                            onClick={() => cancelEditingRanchGirl(girl.id)}
                            className="bg-gray-200 text-gray-700 px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors font-semibold text-sm flex-1 sm:flex-none"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleSaveRanchGirl(girl.id)}
                            className="bg-white text-ggaa-brown px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-sm shadow-md flex-1 sm:flex-none"
                          >
                            Save
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                    {/* Name Fields */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editedGirl.firstName}
                            onChange={(e) => handleRanchGirlInputChange(girl.id, 'firstName', e.target.value)}
                            className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                          />
                        ) : (
                          <p className="text-gray-900 font-medium py-2">{girl.firstName}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editedGirl.lastName}
                            onChange={(e) => handleRanchGirlInputChange(girl.id, 'lastName', e.target.value)}
                            className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                          />
                        ) : (
                          <p className="text-gray-900 font-medium py-2">{girl.lastName}</p>
                        )}
                      </div>
                    </div>

                    {/* Date of Birth */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Birth</label>
                      {isEditing ? (
                        <input
                          type="date"
                          value={editedGirl.dateOfBirth}
                          onChange={(e) => handleRanchGirlInputChange(girl.id, 'dateOfBirth', e.target.value)}
                          className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                        />
                      ) : (
                        <p className="text-gray-900 font-medium py-2">{new Date(girl.dateOfBirth).toLocaleDateString()}</p>
                      )}
                    </div>

                    {/* Read-only fields */}
                    <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-lg p-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1">Skill Level</label>
                        <p className="text-sm font-bold text-ggaa-green">{girl.skillLevel}</p>
                        <p className="text-xs text-gray-500 italic">Admin Only</p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1">Assigned Horse</label>
                        <p className="text-sm font-bold text-ggaa-brown">{girl.assignedHorse?.name || 'Not assigned'}</p>
                      </div>
                      {girl.boardPosition && (
                        <div className="col-span-2">
                          <label className="block text-sm font-semibold text-gray-600 mb-1">Board Position</label>
                          <p className="text-sm font-bold text-purple-600">{girl.boardPosition}</p>
                          <p className="text-xs text-gray-500 italic">Admin Only</p>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1">Member Since</label>
                        <p className="text-sm font-bold text-gray-700">{new Date(girl.memberSince).getFullYear()}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1">Graduation Year</label>
                        <p className="text-sm font-bold text-gray-700">{girl.graduationYear}</p>
                      </div>
                    </div>

                    {/* Emergency Contact */}
                    <div className="border-t-2 border-gray-200 pt-4 space-y-4">
                      <h4 className="font-semibold text-gray-900">Emergency Contact</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                          {isEditing ? (
                            <input
                              type="text"
                              value={editedGirl.emergencyContactName || ''}
                              onChange={(e) => handleRanchGirlInputChange(girl.id, 'emergencyContactName', e.target.value)}
                              className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                            />
                          ) : (
                            <p className="text-gray-900 font-medium py-2">{girl.emergencyContactName || '-'}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                          {isEditing ? (
                            <input
                              type="tel"
                              value={editedGirl.emergencyContactPhone || ''}
                              onChange={(e) => handleRanchGirlInputChange(girl.id, 'emergencyContactPhone', e.target.value)}
                              className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                            />
                          ) : (
                            <p className="text-gray-900 font-medium py-2">{girl.emergencyContactPhone || '-'}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Medical Information */}
                    <div className="border-t-2 border-gray-200 pt-4 space-y-4">
                      <h4 className="font-semibold text-gray-900">Medical Information</h4>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Allergies</label>
                        {isEditing ? (
                          <textarea
                            value={editedGirl.allergies || ''}
                            onChange={(e) => handleRanchGirlInputChange(girl.id, 'allergies', e.target.value)}
                            rows={2}
                            className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                            placeholder="List any allergies..."
                          />
                        ) : (
                          <p className={`py-2 ${girl.allergies ? 'text-red-600 font-medium' : 'text-gray-400 italic'}`}>
                            {girl.allergies || 'None reported'}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Medical Notes</label>
                        {isEditing ? (
                          <textarea
                            value={editedGirl.medicalNotes || ''}
                            onChange={(e) => handleRanchGirlInputChange(girl.id, 'medicalNotes', e.target.value)}
                            rows={3}
                            className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                            placeholder="Any medical conditions or notes..."
                          />
                        ) : (
                          <p className={`py-2 ${girl.medicalNotes ? 'text-blue-600 font-medium' : 'text-gray-400 italic'}`}>
                            {girl.medicalNotes || 'None reported'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl border-2 border-ggaa-green/30 p-12 text-center">
            <p className="text-gray-500 text-lg mb-4">No ranch girls registered yet.</p>
            <p className="text-gray-400">Contact an administrator to add ranch girls to your family.</p>
          </div>
        )}
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 w-full max-w-md mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Change Password</h3>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                <AppIcon name="close" className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handlePasswordChange} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Current Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  required
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter current password"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  New Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter new password (min 6 characters)"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Confirm New Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Confirm new password"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Password Requirements:</strong>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>At least 6 characters long</li>
                    <li>New password must match confirmation</li>
                  </ul>
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  className="flex-1 px-6 py-3 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold shadow-md"
                >
                  Change Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
